/* tu-scroller.js -- the horizontal rail scroller (prev/next arrows, snap
 * paging, at-start/at-end state) for every [data-tu-scroller] in the theme:
 * tu-product-rail, tu-related, tu-brand-rail, tu-directory, tu-top-lines.
 *
 * 2026-09-13: moved here out of tu-product-rail's {% javascript %} block.
 * Shopify compiles section javascript into one file but only RUNS a
 * section's block when that section is on the page (`__sections__[name]`
 * guard in compiled_assets/scripts.js). So the arrows only worked on pages
 * that happened to include a tu-product-rail -- the homepage -- and never on
 * the categories page (tu-directory alone) or, once the product page's
 * best-seller rail was folded into tu-related, on product and cart pages.
 * A theme asset loads everywhere.
 */
class TuScroller {
  constructor(root) {
    this.root = root;
    this.view = root.querySelector('[data-tu-viewport]');
    this.prev = root.querySelector('[data-tu-scroll-prev]');
    this.next = root.querySelector('[data-tu-scroll-next]');
    if (!this.view) return;

    this.onScroll = this.sync.bind(this);
    this.view.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    if (this.prev) this.prev.addEventListener('click', () => this.page(-1));
    if (this.next) this.next.addEventListener('click', () => this.page(1));

    this.sync();
  }

  step() {
    const item = this.view.querySelector('.tu-scroller__item');
    if (!item) return this.view.clientWidth;
    const gap = parseFloat(getComputedStyle(this.view.querySelector('.tu-scroller__track')).gap) || 0;
    const per = Math.max(1, Math.floor(this.view.clientWidth / (item.offsetWidth + gap)));
    return (item.offsetWidth + gap) * per;
  }

  page(dir) {
    this.view.scrollBy({ left: this.step() * dir, behavior: 'smooth' });
  }

  sync() {
    const max = this.view.scrollWidth - this.view.clientWidth;
    const x = this.view.scrollLeft;

    // The viewport carries padding-inline so cards align to the page gutter,
    // and scroll-snap rests the first card past that padding. The resting
    // "start" is therefore the gutter width, not zero.
    const pad = parseFloat(getComputedStyle(this.view).paddingInlineStart) || 0;
    const atStart = x <= pad + 1;
    const atEnd = x >= max - 1;

    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd || max <= 0;
    this.root.dataset.tuMore = (!atEnd && max > 0) ? 'true' : 'false';
    this.root.dataset.tuBack = (!atStart && max > 0) ? 'true' : 'false';
  }
}

function tuScrollerInit() {
  document.querySelectorAll('[data-tu-scroller]').forEach((el) => {
    // A rail whose track rendered no cards is hidden with its heading. Seen
    // 2026-09-13 on the mirror's 404 page: the not-found controller re-runs
    // market detection from the visitor's IP (en-TN here) and in that market
    // context the collection walk yields nothing, so the page showed
    // "Lost? Try these" over an empty strip. Every other template kept the
    // en-US localisation cookie and rendered normally.
    if (!el.querySelector('.tu-scroller__item')) {
      const sec = el.closest('.tu-sec');
      if (sec) sec.hidden = true;
      return;
    }
    if (!el.tuScrollerBound) {
      el.tuScrollerBound = true;
      new TuScroller(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', tuScrollerInit);
} else {
  tuScrollerInit();
}
document.addEventListener('shopify:section:load', tuScrollerInit);
